/* @flow */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Checkbox, Form, Icon, Input } from 'antd';
import { FormComponentProps } from 'antd/es/form';
import { useImmer } from 'use-immer';
import { SubmitButton, TermsAndConditions } from '@/components';
import { isEmailValid } from '@/utils/validation';
import styles from './Login.module.pcss';

const { Item } = Form;

type Props = {
  form: FormComponentProps,
  auth: AuthT,
  resetErrors: () => void,
  logIn: (credential: { email: string, password: string, autoLogin: boolean }) => Promise<any>,
  isPrivacyPolicyModalOpen: boolean,
  isTermsOfServiceModalOpen: boolean,
  isPlatformAgreementsModalOpen: boolean,
  togglePrivacyPolicyModal: (isModalOpen: boolean) => void,
  toggleTermsOfServiceModal: (isModalOpen: boolean) => void,
  togglePlatformAgreementsModal: (isModalOpen: boolean) => void,
  acceptTermsAndPolicy: ({ is_accepted: boolean }) => Promise<any>,
  acceptTermsAndPolicyLoading: boolean,
};

type State = {
  autoLogin: boolean,
  emailValidateError: string,
  emailValidateStatus: string,
};

const Login = ({
  auth,
  form,
  logIn,
  resetErrors,
  togglePrivacyPolicyModal,
  toggleTermsOfServiceModal,
  togglePlatformAgreementsModal,
  isPrivacyPolicyModalOpen,
  isTermsOfServiceModalOpen,
  isPlatformAgreementsModalOpen,
  acceptTermsAndPolicy,
  acceptTermsAndPolicyLoading,
}: Props) => {
  const [state, setState] = useImmer<State>({
    autoLogin: true,
    emailValidateError: '',
    emailValidateStatus: '',
  });

  useEffect(() => {
    resetErrors();
  }, [resetErrors]);

  const handleSubmit = (e) => {
    e.preventDefault();
    form.validateFields({ force: true }, (errors, values) => {
      if (!values.email) {
        setState((draft) => {
          draft.emailValidateError = 'Please input your email.';
          draft.emailValidateStatus = 'error';
        });
      } else if (values.email && !isEmailValid(values.email)) {
        setState((draft) => {
          draft.emailValidateError = 'This email address is formatted incorrectly.';
          draft.emailValidateStatus = 'error';
        });
      } else if (!errors && isEmailValid(values.email)) {
        setState((draft) => {
          draft.emailValidateError = '';
          draft.emailValidateStatus = '';
        });
        logIn({ ...values, autoLogin: state.autoLogin });
      }
    });
  };

  const changeAutoLogin = (e) => {
    setState((draft) => {
      draft.autoLogin = e.target.checked;
    });
  };

  return (
    <div className={styles.root} data-cy="login-form">
      <Form onSubmit={handleSubmit} className={styles.login}>
        <Item help={state.emailValidateError} validateStatus={state.emailValidateStatus}>
          {form.getFieldDecorator('email')(
            <Input
              prefix={<Icon type="mail" />}
              type="email"
              size="large"
              placeholder="Enter your company email"
              data-cy="login-email-input"
            />
          )}
        </Item>
        <Item>
          {form.getFieldDecorator('password', {
            rules: [{ required: true, message: 'Please enter your password.' }],
          })(
            <Input.Password
              prefix={<Icon type="lock" />}
              type="password"
              size="large"
              placeholder="Enter your password"
              data-cy="login-password-input"
            />
          )}
        </Item>
        <Item display-if={auth.loginError}>
          <Alert message={auth.loginError} type="error" data-cy="login-error-message" />
        </Item>
        <Item>
          <Checkbox className={styles.remember} checked={state.autoLogin} onChange={changeAutoLogin}>
            Remember me
          </Checkbox>
          <Link className={styles.passwordForgot} to="/password-reset">
            Forgot your password?
          </Link>
        </Item>
        <SubmitButton loading={auth.isLogIn} name="login" className={styles.submit} data-cy="login-button" />
      </Form>
      {isPrivacyPolicyModalOpen || isTermsOfServiceModalOpen || isPlatformAgreementsModalOpen ? (
        <TermsAndConditions
          isPrivacyPolicyModalOpen={isPrivacyPolicyModalOpen}
          isTermsOfServiceModalOpen={isTermsOfServiceModalOpen}
          isPlatformAgreementsModalOpen={isPlatformAgreementsModalOpen}
          togglePrivacyPolicyModal={togglePrivacyPolicyModal}
          toggleTermsOfServiceModal={toggleTermsOfServiceModal}
          togglePlatformAgreementsModal={togglePlatformAgreementsModal}
          termsPolicyPayload={auth.termsPolicyPayload}
          acceptTermsAndPolicy={acceptTermsAndPolicy}
          acceptTermsAndPolicyLoading={acceptTermsAndPolicyLoading}
        />
      ) : null}
    </div>
  );
};

export default Form.create({ name: 'loginForm' })(Login);
